// ABOUTME: Shop selector page displayed in the main content area when no shop is selected.
// ABOUTME: Shows shops as clickable cards with map, name, address, and opening hours.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Card, CardActionArea, Chip } from '@mui/material';
import { AccessTime as AccessTimeIcon, Storefront as StorefrontIcon } from '@mui/icons-material';
import { colors } from '../theme/colors';

function ShopSelector({ shops, onSelectShop }) {
  const { t } = useTranslation();

  const getGoogleMapsStaticUrl = (address) => {
    const encoded = encodeURIComponent(address);
    return `https://maps.googleapis.com/maps/api/staticmap?center=${encoded}&zoom=14&size=400x200&markers=color:red%7C${encoded}&key=AIzaSyCsgtD1z4L3ayLdl0hRQp7E2Vu78LW51Ms`;
  };

  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = {
    monday: t('common.mon'),
    tuesday: t('common.tue'),
    wednesday: t('common.wed'),
    thursday: t('common.thu'),
    friday: t('common.fri'),
    saturday: t('common.sat'),
    sunday: t('common.sun'),
  };

  const formatHoursRange = (dayHours) => {
    if (!dayHours) return 'Zárva';
    return `${dayHours.opening} - ${dayHours.closing}`;
  };

  const isToday = (dayName) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[new Date().getDay()] === dayName;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: colors.text.primary,
            mb: 1,
          }}
        >
          {t('common.shopSelector')}
        </Typography>
        <Typography
          sx={{
            color: colors.text.secondary,
            fontSize: '1rem',
          }}
        >
          {t('common.shopSelectorSubtitle')}
        </Typography>
      </Box>

      {/* Shop cards grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 3,
        }}
      >
        {shops.map((shop) => (
            <Card
              key={shop.shop_id}
              sx={{
                borderRadius: '16px',
                border: `1px solid ${colors.border.light}`,
                boxShadow: 'none',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: colors.ui.accent,
                  boxShadow: '0 4px 20px rgba(249, 123, 44, 0.15)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardActionArea
                onClick={() => onSelectShop(shop.shop_id)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  height: '100%',
                }}
              >
                {/* Map area */}
                {shop.address && (
                  <Box
                    sx={{
                      width: '100%',
                      height: 160,
                      bgcolor: colors.background.light,
                      backgroundImage: `url(${getGoogleMapsStaticUrl(shop.address)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                )}

                {/* Shop info */}
                <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  {/* Shop name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        color: colors.text.primary,
                      }}
                    >
                      {shop.name || shop.shop_id}
                    </Typography>
                    {shop.virtual && (
                      <Chip
                        icon={<StorefrontIcon />}
                        label={t('common.virtualShop')}
                        size="small"
                        sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 500, '& .MuiChip-icon': { color: '#1d4ed8' } }}
                      />
                    )}
                  </Box>

                  {/* Member shop count for virtual shops */}
                  {shop.virtual && shop.members?.length > 0 && (
                    <Typography sx={{ fontSize: '0.875rem', color: colors.text.secondary, mb: 1.5 }}>
                      {t('common.memberCount', { n: shop.members.length })}
                    </Typography>
                  )}

                  {/* Address */}
                  {shop.address && (
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: colors.text.secondary,
                        mb: 1.5,
                      }}
                    >
                      {shop.address}
                    </Typography>
                  )}

                  {/* Spacer to push hours to bottom */}
                  <Box sx={{ flexGrow: 1 }} />

                  {/* Weekly opening hours */}
                  {shop.hours && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      {dayOrder.map((day) => {
                        const dayHours = shop.hours[day];
                        const today = isToday(day);
                        return (
                          <Box
                            key={day}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              fontSize: '0.75rem',
                              color: today ? colors.text.primary : colors.text.muted,
                              fontWeight: today ? 600 : 400,
                            }}
                          >
                            {today ? (
                              <AccessTimeIcon sx={{ fontSize: 14, color: colors.text.primary }} />
                            ) : (
                              <Box sx={{ width: 14 }} />
                            )}
                            <Box sx={{ width: 55 }}>{dayLabels[day]}</Box>
                            <Box>{formatHoursRange(dayHours)}</Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </CardActionArea>
            </Card>
        ))}
      </Box>
    </Box>
  );
}

export default ShopSelector;
